import { Alert, FormControl, FormControlLabel, Radio, RadioGroup, Stack, TextField } from '@mui/material'
import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { MarketingButton } from '~/components/common/MarketingButton'
import { SimpleDialog } from '~/components/common/SimpleDialog'
import { getPrefectureNameJpByPref } from '~/lib/prefectureHelper'
import { snackActions } from '~/lib/snackbarHelper'
import { POSTAL_CODE_WITHOUT_HYPHEN_REGEXP } from '~/lib/constants'
import { Pref, useCampaignShopLocationsLazyQuery } from '~/generated/graphql'
import { CreateCampaignState } from './state'
import { ValidationErrorMessage } from '~/components/common/ValidationErrorMessage'

type AddressCandidate = {
  prefectureCode: Pref
  shopCityCode: string
  cityName: string
  town: string
}

type PostalCodeSearchProps = {
  addressFetchError: boolean
  setAddressFetchError: (value: boolean) => void
  postalCodeNoResults: boolean
  setPostalCodeNoResults: (value: boolean) => void
}

export const PostalCodeSearch = ({
  addressFetchError,
  setAddressFetchError,
  postalCodeNoResults,
  setPostalCodeNoResults,
}: PostalCodeSearchProps) => {
  const { control, getValues, setValue, clearErrors } = useFormContext<CreateCampaignState>()
  const [getCampaignShopLocations, { loading: loadingShopLocations }] = useCampaignShopLocationsLazyQuery()

  // 住所選択モーダル
  const [addressModalOpen, setAddressModalOpen] = useState(false)
  const [addressCandidates, setAddressCandidates] = useState<AddressCandidate[]>([])
  const [selectedAddress, setSelectedAddress] = useState<string>('')

  const handleAddressSelect = () => {
    const index = parseInt(selectedAddress)
    const selected = addressCandidates[index]
    if (!selected) {
      setAddressFetchError(true)
      return
    }

    setValue('shopPref', selected.prefectureCode)
    // cityName + town を市区町村名に設定
    setValue('cityName', selected.cityName + selected.town)
    clearErrors(['shopPref', 'cityName'])
    setAddressModalOpen(false)
  }

  const onAddressCompleteButtonClick = async () => {
    const postalCode = getValues('shopPostalCode')
    if (!postalCode || !POSTAL_CODE_WITHOUT_HYPHEN_REGEXP.test(postalCode)) {
      snackActions.error('郵便番号は半角数字7桁で入力してください')
      return
    }

    setAddressFetchError(false)
    setPostalCodeNoResults(false)

    try {
      const { data, error } = await getCampaignShopLocations({
        variables: { input: { postalCode } },
      })

      if (error) throw error

      const locations = data?.campaignShopLocations ?? []

      if (locations.length === 0) {
        setPostalCodeNoResults(true)
        return
      }

      if (locations.length === 1) {
        const location = locations[0]
        // lengthをチェックしているのでありえないが、型のために確認
        if (!location) {
          throw new Error('location not found')
        }

        setValue('shopPref', location.prefectureCode)
        // cityName + town を市区町村名に設定
        setValue('cityName', location.cityName + location.town)
        clearErrors(['shopPref', 'cityName'])
        return
      }

      // 複数の結果がある場合、モーダルを表示
      setAddressCandidates(
        locations.map((loc) => ({
          prefectureCode: loc.prefectureCode,
          shopCityCode: loc.shopCityCode,
          cityName: loc.cityName,
          town: loc.town,
        })),
      )
      setSelectedAddress('0')
      setAddressModalOpen(true)
    } catch {
      setAddressFetchError(true)
    }
  }

  return (
    <>
      <FormControl fullWidth>
        <Stack gap={0.5}>
          {addressFetchError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              住所の自動入力に失敗しました。お手数ですが、都道府県と市区町村を手動で入力してください。
            </Alert>
          )}
          {postalCodeNoResults && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              郵便番号から住所が見つかりませんでした。入力内容に誤りがないかご確認のうえ、再度お試しください。
              <br />
              それでも見つからない場合は、お手数ですが都道府県と市区町村を手動で入力してください。
            </Alert>
          )}
          <Stack direction="row" spacing={2} alignItems="center">
            <Controller
              name="shopPostalCode"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="郵便番号"
                  placeholder="1200006"
                  error={!!error?.message}
                  helperText={<ValidationErrorMessage error={error?.message} />}
                  inputMode="numeric"
                  inputRef={field.ref}
                  slotProps={{
                    inputLabel: { shrink: !!field.value },
                  }}
                />
              )}
            />
            <MarketingButton
              size="small"
              variant="contained"
              onClick={onAddressCompleteButtonClick}
              loading={loadingShopLocations}
            >
              住所を自動入力する
            </MarketingButton>
          </Stack>
        </Stack>
      </FormControl>

      {/* 住所選択モーダル */}
      <SimpleDialog
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        onCancel={() => setAddressModalOpen(false)}
        onOk={handleAddressSelect}
        title="住所を選択してください"
      >
        <FormControl fullWidth>
          <RadioGroup value={selectedAddress} onChange={(e) => setSelectedAddress(e.target.value)}>
            {addressCandidates.map((candidate, index) => (
              <FormControlLabel
                key={index}
                value={String(index)}
                control={<Radio />}
                label={`${getPrefectureNameJpByPref(candidate.prefectureCode) || ''}${candidate.cityName}${candidate.town}`}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </SimpleDialog>
    </>
  )
}
